import type { ParamSchema, ParamValues } from './types';

/**
 * Scales every length-valued parameter for a different render resolution.
 *
 * Exporting at 3× means the canvas is three times larger, and a 2 px stroke has to
 * become 6 px or the artwork is not the same artwork — it is the same drawing with
 * hairlines. Rather than sprinkling `* ctx.scale` through every technique, where a new
 * technique would inevitably forget one, the rule lives in one place:
 *
 *   **A parameter declared with `unit: 'px'` is a length and scales with the output.
 *   A parameter declared with `unit: '1/px'` is an inverse length and scales inversely.**
 *
 * The inverse case exists because some parameters multiply a coordinate rather than
 * being one — a noise frequency of 0.0022 applied to `x` has to halve when `x` doubles,
 * or the pattern comes out at half the size in the export.
 *
 * Everything else — counts, probabilities, angles, ratios, fractions of the canvas —
 * is resolution independent by construction and passes through untouched. So are
 * positions, because techniques place elements as fractions of the canvas or via
 * `rng * width`.
 *
 * This covers text settings, layer parameters, the background and the post-processing
 * chain, since all four are described by the same schema type.
 */
export const PX_UNIT = 'px';
export const INVERSE_PX_UNIT = '1/px';

export function scaleParamValues(schema: ParamSchema, values: ParamValues, scale: number): ParamValues {
  if (scale === 1) return values;

  const out: ParamValues = { ...values };
  for (const param of schema) {
    const isRange = param.kind === 'range';
    if (param.kind !== 'float' && param.kind !== 'int' && !isRange) continue;

    const factor = param.unit === PX_UNIT ? scale : param.unit === INVERSE_PX_UNIT ? 1 / scale : 0;
    if (factor === 0) continue;

    const v = values[param.key];
    if (isRange) {
      if (!Array.isArray(v) || v.length !== 2) continue;
      const lo = v[0];
      const hi = v[1];
      if (typeof lo !== 'number' || typeof hi !== 'number') continue;
      out[param.key] = [lo * factor, hi * factor];
    } else {
      if (typeof v !== 'number') continue;
      out[param.key] = param.kind === 'int' ? Math.round(v * factor) : v * factor;
    }
  }
  return out;
}
